import { useState, type FormEvent } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { contactChannels } from "@/mocks/digData";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahfn5boh653ivfvntjg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    payload.delete("website_alt");

    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;

      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
          <div>
            <SectionHeading
              eyebrow="CONTACT"
              title="联系我们 · 投稿与线索"
              desc="有新的瓜、新的反转，或者发现站内内容需要更正？把线索留给我们，编辑部会尽快核实处理。"
            />

            <div className="mt-8 flex flex-col gap-4">
              {contactChannels.map((channel) => (
                <div
                  key={channel.id}
                  className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500/10">
                    <i className={`${channel.icon} text-xl text-primary-600`} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{channel.label}</p>
                    <p className="text-sm md:text-base font-medium text-foreground-900 break-all">
                      {channel.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
              <p className="flex items-start gap-2 text-sm text-secondary-900 leading-relaxed">
                <i className="ri-information-line mt-0.5 shrink-0" />
                我们只跟进可多方核实的公开信息。涉及个人隐私、未经证实的指控类内容，恕不直接发布。
              </p>
            </div>
          </div>

          <form
            data-readdy-form
            id="tip-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
          >
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              留下你的线索
            </h3>
            <p className="mt-1 text-sm text-foreground-500">带 * 为必填项</p>

            <div className="mt-5 flex flex-col gap-4">
              <div>
                <label htmlFor="nickname" className="block text-sm font-medium text-foreground-800">
                  怎么称呼你 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="nickname"
                  name="nickname"
                  type="text"
                  required
                  placeholder="网名或昵称都可以"
                  className="mt-2 w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
                  联系邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于核实线索，不会公开"
                  className="mt-2 w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="tip_type" className="block text-sm font-medium text-foreground-800">
                  线索类型 <span className="text-primary-600">*</span>
                </label>
                <select
                  id="tip_type"
                  name="tip_type"
                  required
                  defaultValue=""
                  className="mt-2 w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                >
                  <option value="" disabled>
                    请选择线索类型
                  </option>
                  <option value="新瓜爆料">新瓜爆料</option>
                  <option value="后续反转补充">后续反转补充</option>
                  <option value="当事人发声线索">当事人发声线索</option>
                  <option value="官方回应来源">官方回应来源</option>
                  <option value="内容更正申请">内容更正申请</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="detail" className="block text-sm font-medium text-foreground-800">
                  线索描述 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="detail"
                  name="detail"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="用几句话说明事件、时间点与信息来源（最多 500 字）"
                  className="mt-2 w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
                <p className="mt-1 text-xs text-foreground-500">请尽量说明可核实的公开来源。</p>
              </div>

              <input
                className="form-honeypot-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "submitting" ? "提交中…" : "提交线索"}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line text-lg" />
                  已收到你的线索，编辑部会尽快核实，谢谢你的投喂。
                </p>
              )}

              {status === "error" && (
                <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-900">
                  <i className="ri-error-warning-line text-lg" />
                  {formError || "提交失败，请稍后再试。"}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}